import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useTeams } from '@/hooks/api/0180_teams';
import { useUserInvitationsInvalidate } from '@/hooks/api/0470_user_invitations';
import { useCreateUserInvitation } from '@/hooks/api/0500_create_user_invitations';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { MultiValue } from 'react-select';

type FormInputs = {
  identifier: string;
  email: string;
  admin: boolean;
  team_ids: number[];
  approver_team_ids: number[];
  financial_officer_team_ids: number[];
  backofficer_team_ids: number[];
};

export const WorkspaceNewInvitation = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('ユーザーの招待'), []);
  useEffect(
    () => setBreadcrumbs([{ label: `スペースユーザー設定`, path: `/w/${_w_!.identifier}/setting/members` }]),
    []
  );

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { data: teams } = useTeams({ identifier: _w_!.identifier });
  const { mutate, data, isPending, error } = useCreateUserInvitation();
  const { invalidate } = useUserInvitationsInvalidate(_w_!.identifier);
  const [teamOptions, setTeamOptions] = useState<SelectOptionType[] | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: { identifier: _w_!.identifier, admin: false, team_ids: [] },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('招待を送信しました');
      invalidate(); // UserInvitationsのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/members`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (teams) setTeamOptions(teams.map((d): SelectOptionType => ({ value: d.id, label: d.name })));
  }, [teams]);

  if (teamOptions == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'member'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-12">
                  <h5 className="mb-0">ユーザーの招待</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>
                      メールアドレス<span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      maxLength={100}
                      className={`form-control ${errors.email && 'is-invalid'}`}
                      placeholder={'メールアドレス'}
                      {...register('email', {
                        required: '入力して下さい。',
                        pattern: {
                          value: /^[\w\-._+]+@[\w\-._]+\.[A-Za-z]+/,
                          message: 'メールアドレスが不正です。',
                        },
                      })}
                    />
                    {errors.email && <div className="text-xs text-danger mt-1">{errors.email.message}</div>}
                  </div>

                  <div className="form-group">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="admin" {...register('admin')} />
                      <label className="form-check-label" htmlFor="admin">
                        スペースの管理者として招待する
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>メンバーとして参加するチーム</label>
                    <SelectMulti
                      options={teamOptions}
                      selectedIds={getValues('team_ids')}
                      placeholder={'参加するチームを選択してください。'}
                      classes={'mb-1'}
                      onChange={(mv: MultiValue<SelectOptionType>) => {
                        if (mv) {
                          setValue(
                            'team_ids',
                            mv.map((d) => d.value)
                          );
                        } else {
                          resetField('team_ids');
                        }
                      }}
                    />
                    <div className="text-xs text-muted mt-0">
                      メンバーはチームのプロジェクトと関連タスクにアクセスできます。
                    </div>
                  </div>

                  <div className="form-group">
                    <label>承認者として参加するチーム</label>
                    <SelectMulti
                      options={teamOptions}
                      selectedIds={getValues('approver_team_ids')}
                      placeholder={'参加するチームを選択してください。'}
                      classes={'mb-1'}
                      onChange={(mv: MultiValue<SelectOptionType>) => {
                        if (mv) {
                          setValue(
                            'approver_team_ids',
                            mv.map((d) => d.value)
                          );
                        } else {
                          resetField('approver_team_ids');
                        }
                      }}
                    />
                    <div className="text-xs text-muted mt-0">
                      承認者はメンバー権限に加え契約と見積のタスクの承認を行うことができます。
                    </div>
                  </div>

                  <div className="form-group">
                    <label>経理担当者として参加するチーム</label>
                    <SelectMulti
                      options={teamOptions}
                      selectedIds={getValues('financial_officer_team_ids')}
                      placeholder={'参加するチームを選択してください。'}
                      classes={'mb-1'}
                      onChange={(mv: MultiValue<SelectOptionType>) => {
                        if (mv) {
                          setValue(
                            'financial_officer_team_ids',
                            mv.map((d) => d.value)
                          );
                        } else {
                          resetField('financial_officer_team_ids');
                        }
                      }}
                    />
                    <div className="text-xs text-muted mt-0">
                      経理担当者はメンバー権限に加えプロジェクトの管理会計の編集を行えます
                    </div>
                  </div>

                  <div className="form-group">
                    <label>バックオフィス担当者として参加するチーム</label>
                    <SelectMulti
                      options={teamOptions}
                      selectedIds={getValues('backofficer_team_ids')}
                      placeholder={'参加するチームを選択してください。'}
                      classes={'mb-1'}
                      onChange={(mv: MultiValue<SelectOptionType>) => {
                        if (mv) {
                          setValue(
                            'backofficer_team_ids',
                            mv.map((d) => d.value)
                          );
                        } else {
                          resetField('backofficer_team_ids');
                        }
                      }}
                    />
                    <div className="text-xs text-muted mt-0">
                      バックオフィス担当者はプロジェクト情報そのものにアクセスできず、関連するタスクにのみアクセスすることができます。
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>招待する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
